import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Route,
  Switch,
  useHistory,
  useLocation,
} from 'react-router-dom';

import { BRAND_COLORS } from 'brand-guidelines/colors';
import {
  loginUserWithToken,
  requestClinic,
  requestUserAndPatient,
  resetMatchInsurance,
  updateBookingWidgetSlot,
  updateDirectToProfileLink,
  updateDMProps,
  updateInsuranceProviderAndPlan,
  updateMatchAndResults,
  updateSelectedAppointment,
} from 'connect/actions';
import { UserSourceStateHandler } from 'connect/state/handlers/user';
import CheckoutModal from 'pages/legacy-checkout/components/common/CheckoutModal/CheckoutModal';
import Profile from 'pages/profile/components/Profile/Profile';
import { googlePlaceActionLink } from 'utils/google';
import { decodeBase64ToObject } from 'utils/string-utils';

import {
  selectDataClinicCountry,
  selectDataClinicLocality,
  selectDataMatchId,
} from '../../connect/selectors';
import * as Url from '../../pages/common/helpers/url-helpers';
import { FunnelProvider } from '../../pages/funnel/storage/Storage';
import { useWindowDimensions } from '../../providers/WindowDimensionsProvider';

import { AppContainer } from './styled-components';

/*
 * ProfilePage
 *
 * This is the profile route, at the '/results/:clinicId' route.
 */
const ProfilePage = ({ match }) => {
  const { clinicId: clinicIdString } = match.params;
  const dispatch = useDispatch();
  const history = useHistory();
  const { layout } = useWindowDimensions();
  const { search, pathname } = useLocation();

  const storeMatchId = useSelector(selectDataMatchId);

  const [

    utmSource,
    profileLink,
    bookingWidgetSlotProvider,
    bookingWidgetSlotDate,
    bookingWidgetSlotTime,
    dm_promo_code,
    dm_referral_code,
    dm_referral_promo,
    referral_code_deprecated,
    referral_promo_deprecated,
    authParam,
    paramRequestData,
    paramRequestedAppt,
    paramInsurance,
    paramMatch,

  ] = Url.getParam(
    search,
    'utm_source',
    'profileLink',
    'bookingWidgetSlotProvider',
    'bookingWidgetSlotDate',
    'bookingWidgetSlotTime',
    'dm_promo_code',
    'dm_referral_code',
    'dm_referral_promo',
    'referral_code', // ensure backwards compatability with old links
    'referral_promo', // ensure backwards compatability with old links
    'auth',
    'encodedRequestData',
    'encodedRequestedAppt',
    'encodedInsurance',
    'encodedMatch',
  );

  useEffect(() => {
    googlePlaceActionLink.captureConversionParams(search, clinicIdString);
  }, []);

  // Handle magic link to return to checkout
  useEffect(() => {
    if (utmSource) {
      dispatch(UserSourceStateHandler.update(utmSource));
    }

    if (authParam) {
      dispatch(loginUserWithToken(authParam, true));
    }

    if (paramRequestedAppt) {
      const { providerId, slotId, anyProviderSelected } = decodeBase64ToObject(
        paramRequestedAppt,
      );
      dispatch(
        updateSelectedAppointment(
          parseInt(providerId, 10),
          parseInt(slotId, 10),
          null,
          anyProviderSelected,
        ),
      );
    }

    // Restore promo code data
    if (paramRequestData) {
      const requestDataObject = decodeBase64ToObject(paramRequestData);

      const promoCode = requestDataObject?.dmProps?.promo_code;
      const referralCode = requestDataObject?.dmProps?.referral_code;
      const referralPromo = requestDataObject?.dmProps?.referral_promo;

      if (promoCode || referralCode || referralPromo) {
        dispatch(updateDMProps(promoCode, referralCode, referralPromo));
      }
    }

    if (paramInsurance) {
      const insurance = decodeBase64ToObject(paramInsurance);
      dispatch(updateInsuranceProviderAndPlan(insurance));
    }

    // Update match with revisit link match if it's different
    if (paramMatch) {
      const paramMatchObject = decodeBase64ToObject(paramMatch);
      if (paramMatchObject.id !== storeMatchId) {
        dispatch(
          updateMatchAndResults({
            match: decodeBase64ToObject(paramMatch),
            results: [],
          }),
        );
      }
    }
  }, [utmSource, paramRequestedAppt, paramInsurance, authParam, paramMatch]);

  const referral_code = dm_referral_code || referral_code_deprecated;
  const referral_promo = dm_referral_promo || referral_promo_deprecated;

  window.OCOptimizelyData = {
    country: useSelector(selectDataClinicCountry),
    locality: useSelector(selectDataClinicLocality),
  };

  useEffect(() => {
    if (profileLink) {
      dispatch(updateDirectToProfileLink(true));

      if (dm_promo_code || referral_code || referral_promo) {
        dispatch(updateDMProps(dm_promo_code, referral_code, referral_promo));
      }
      if (!paramInsurance) {
        dispatch(resetMatchInsurance());
      }
      dispatch(requestUserAndPatient());
    }
  }, [profileLink]);

  useEffect(() => {
    if (
      bookingWidgetSlotProvider
      && bookingWidgetSlotDate
      && bookingWidgetSlotTime
      && (profileLink || history.action !== 'POP')
    ) {
      dispatch(
        updateBookingWidgetSlot(
          parseInt(bookingWidgetSlotProvider, 10),
          bookingWidgetSlotDate,
          parseInt(bookingWidgetSlotTime, 10),
        ),
      );
    }
  }, [
    history,
    bookingWidgetSlotProvider,
    bookingWidgetSlotDate,
    bookingWidgetSlotTime,
  ]);

  useEffect(() => {
    if (clinicIdString.length > 0) {
      dispatch(requestClinic([parseInt(clinicIdString, 10)]));
    }
  }, [clinicIdString]);

  useEffect(() => {
    const checkoutPath = `${match.url}/checkout`;
    if (pathname === checkoutPath || pathname === `${checkoutPath}/`) {
      history.push(`${checkoutPath}/patient`);
    }
  }, [pathname, match.url, history]);

  return (
    <FunnelProvider>
      <AppContainer background={BRAND_COLORS.white}>
        <Profile layout={layout} clinicIdString={clinicIdString} />
        <Switch>
          <Route
            component={CheckoutModal}
            path={`${match.path}/checkout/:checkoutStep`}
          />
        </Switch>
      </AppContainer>
    </FunnelProvider>
  );
};

ProfilePage.propTypes = {
  match: PropTypes.object.isRequired,
};

export default ProfilePage;
